import React, { MutableRefObject, ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnOptions, Datum, Options, Plot } from '@antv/g2plot'
import { ChartPivotRow, SeriesNamesColumn } from '@cubejs-client/core'
import { ColumnChart, ColumnChartProps } from '@opd/g2plot-react'
import truncate from 'lodash/truncate'
import moment from 'moment'

import { ReportType } from '@cozero/models'

import useGraphs from '@/hooks/useGraphs'
import { columnStyles, legendStyles, padding, xAxisStyles, yAxisStyles } from '@/styles/graphs'
import { formatNumber } from '@/utils/number'

interface GraphInput {
  x: string | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  type: string | undefined
}

const Bar = React.memo(
  ({
    chartData,
    height,
    keys,
    visible,
    disableSlider,
    showLegend,
    switchAxis,
    isStack,
    dimensionType,
    timeGranularity,
    chartRef,
    svgChartRef,
    colorFunction,
  }: {
    chartData: ChartPivotRow[]
    height: number | null
    keys: SeriesNamesColumn[]
    visible: boolean
    dimensionType?: string
    disableSlider?: boolean
    showLegend: boolean
    switchAxis: boolean
    isStack: boolean
    timeGranularity?: 'month' | 'quarter' | 'year'
    chartRef?: MutableRefObject<Plot<Options> | null>
    svgChartRef?: MutableRefObject<Plot<Options> | null>
    colorFunction?: (datum: Datum) => string
  }): ReactElement | null => {
    const { graphStyles, customTooltip } = useGraphs({
      graphType: ReportType.BAR,
      switchAxis,
      dimensionType,
    })
    const [isGroup, setIsGroup] = useState(false)
    const [barProps, setBarProps] = useState<ColumnOptions>({ data: [], xField: 'x', yField: 'y' })
    const { t, i18n } = useTranslation('common')

    const config: Omit<ColumnChartProps, 'data' | 'xField'> = {
      appendPadding: padding,
      xAxis: {
        label: {
          formatter: (item) =>
            moment(item).isValid() && item.match(/^\d/)
              ? `${
                  timeGranularity === 'month' || !timeGranularity
                    ? moment(item).format('MMM YYYY')
                    : timeGranularity === 'quarter'
                    ? `Q${moment(item).quarter()} ${moment(item).year()}`
                    : moment(item).year()
                }`
              : truncate(item, { length: 24 }),
          autoHide: true,
          autoRotate: false,
          style: {
            ...xAxisStyles.label?.style,
          },
          offset: 15,
        },
      },
      yField: 'value',
    }

    const camelCaseToSentence = (text: string): string => {
      const result = text.replace(/([A-Z])/g, ' $1')
      return result.charAt(0).toUpperCase() + result.slice(1)
    }

    useEffect(() => {
      const isGroupAux = isStack ? !isStack : chartData?.[0]?.xValues?.length > 1
      setIsGroup(isGroupAux)
    }, [isStack, chartData])

    useEffect(() => {
      const data: GraphInput[] = chartData.reduce((acc: GraphInput[], curr) => {
        let tmp = [
          {
            x: curr.xValues?.at(0),
            value: curr[keys[0].yValues.at(-1) ?? ''],
            type: curr.xValues?.at(-1) || curr.xValues?.at(0),
          },
        ]
        if (keys.length > 1 && Object.keys(curr).length > 3) {
          tmp = keys.map((key) => ({
            x: curr.xValues?.at(0),
            value: curr[key.yValues[0]],
            type: camelCaseToSentence(key.key.split('.').at(-1) as string),
          }))
        }
        return [...acc, ...tmp]
      }, [])
      const barPropsAux: ColumnOptions = {
        ...graphStyles,
        ...config,
        height: height as number,
        legend: showLegend
          ? {
              ...legendStyles,
              itemName: {
                style: legendStyles.itemName?.style,
                formatter: (name) =>
                  moment(name, moment.ISO_8601).isValid()
                    ? `${
                        timeGranularity === 'month' || !timeGranularity
                          ? moment(name).format('MMM YYYY')
                          : timeGranularity === 'quarter'
                          ? `Q${moment(name).quarter()} ${moment(name).year()}`
                          : moment(name).year()
                      }`
                    : name,
              },
            }
          : false,
        isStack,
        yAxis: {
          field: 'value',
          ...yAxisStyles,
          title: {
            style: yAxisStyles.title?.style,
            text: i18n.exists(`common:reports.legend.${keys[0].key}`)
              ? t(`reports.legend.${keys[0].key}`)
              : t('tonnes-emitted'),
          },
        },
        columnStyle: isStack ? columnStyles : { radius: columnStyles.radius },
        isGroup,
        marginRatio: 0,
        maxColumnWidth: 40,
        seriesField: isGroup ? (switchAxis ? 'x' : 'type') : 'type',
        xField: switchAxis ? 'type' : 'x',
        data,
        tooltip: {
          ...customTooltip,
          formatter: (item) => {
            let x = item.x
            let name = item.type || item.x
            if (switchAxis) {
              x = item.type
              name = item.x
            }
            return {
              name: `${moment(name, moment.ISO_8601, true).isValid() ? moment(name).year() : name}`,
              value: `${formatNumber(item.value)} ${
                i18n.exists(`common:reports.legend.${keys[0].key}`)
                  ? t(`reports.legend.${keys[0].key}`)
                  : t('tonnes-emitted')
              }`,
              title: `${moment(x, moment.ISO_8601, true).isValid() ? moment(x).year() : x}`,
            }
          },
        },
        brush: {
          enabled: disableSlider,
          action: 'filter',
        },
        ...(colorFunction && { color: colorFunction }), // overwriting value with undefined kills the default theme
      }
      setBarProps(barPropsAux)
    }, [
      chartData,
      isStack,
      disableSlider,
      switchAxis,
      keys,
      showLegend,
      dimensionType,
      graphStyles,
    ])

    if (!height) {
      return null
    }

    return (
      <>
        <ColumnChart
          height={height}
          {...barProps}
          renderer={'canvas'}
          style={{ display: visible ? 'block' : 'none' }}
          chartRef={(ref) => {
            if (ref && chartRef) {
              chartRef.current = ref as unknown as Plot<Options>
            }
          }}
        />
        <ColumnChart
          height={height}
          {...barProps}
          style={{ display: 'none' }}
          renderer={'svg'}
          width={1000}
          chartRef={(ref) => {
            if (ref && svgChartRef) {
              svgChartRef.current = ref as unknown as Plot<Options>
            }
          }}
        />
      </>
    )
  },
)
Bar.displayName = 'Bar'

export default Bar
